/* @flow */
import cx from 'classnames';
import invariant from 'invariant';
import * as React from 'react';
import { Container } from 'flux/utils';

import Button from 'components/Button.jsx';
import CurrentUser from 'auth/CurrentUser';
import Checkbox from 'components/Checkbox.jsx';
import Columns2 from 'components/Columns2.jsx';
import Divider from 'components/Divider.jsx';
import Field from 'components/Field.jsx';
import GroupCreateModal from 'groups/components/GroupCreateModal.jsx';
import Input from 'components/Input.jsx';
import Label from 'components/Label.jsx';
import Legend from 'components/Legend.jsx';
import Link from 'components/Link.jsx';
import LinkConstants from 'constants/LinkConstants';
import ModalTrigger from 'components/ModalTrigger.jsx';
import Row from 'components/Row.jsx';
import Select from 'components/Select.jsx';
import Widgets from 'collections/Widgets';
import SettingsContainer from 'settings/components/SettingsContainer.jsx';
import Text from 'components/Text.jsx';
import WidgetActions from 'settings/actions/WidgetActions';
import ClipboardTrigger from 'components/ClipboardTrigger.jsx';
import WidgetStore from 'settings/stores/WidgetStore';
import waitForPromises from 'lib/waitForPromises.jsx';
import getRedirectLocationAfterGroupCreation from 'utils/getRedirectLocationAfterGroupCreation';
import ToastActions from 'actions/ToastActions';
import IframePreview from 'components/IframePreview.jsx';
import Radio from 'components/Radio.jsx';
import PropTypes from 'prop-types';
import qs from 'query-string';

import './styles/WidgetSettingsView.scss';

type State = {
  widgets: Widgets,
  type: 'regular' | 'google' | 'wix',
  selected?: number,
  widgetHeight: number,
  withJoinButton: boolean,
  advancedSettingsVisible: boolean,
  canCreateGroups: boolean,
};

class WidgetSettingsView extends React.Component<$FlowFixMeProps, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  static getStores() {
    return [WidgetStore, CurrentUser];
  }

  static routePrefetch = [
    function() {
      return WidgetActions.loadWidgets();
    },
  ];
  static calculateState = () => {
    const widgets = WidgetStore.getAll();
    const currentUser = CurrentUser.getUser();

    return {
      widgets,
      type: 'regular',
      widgetHeight: 500,
      withJoinButton: true,
      selected: widgets.first() ? widgets.first().id : undefined,
      advancedSettingsVisible: false,
      canCreateGroups: currentUser.canCreateGroups(),
    };
  };
  handlerCodeCopy = () => {
    ToastActions.showSuccessToast('Code copied to your clipboard.');
  };
  handlerJoinButtonToggle = () => {
    this.setState({ withJoinButton: !this.state.withJoinButton });
  };
  handlerWidgetChange = id => {
    this.setState({ selected: id });
  };
  handlerTypeChange = type => {
    this.setState({ type });
  };
  handleGroupCreated = group => {
    this.context.router.push(getRedirectLocationAfterGroupCreation(group));
  };
  handleToggleAdvancedSettings = () => {
    this.setState({ advancedSettingsVisible: !this.state.advancedSettingsVisible });
  };
  handleWidgetHeightChange = event => {
    this.setState({ widgetHeight: Number(event.target.value) });
  };
  onRegularTypeSelect = () => this.setState({ type: 'regular' });
  onGoogleTypeSelect = () => this.setState({ type: 'google' });
  onWixTypeSelect = () => this.setState({ type: 'wix' });

  renderBuilder() {
    const widgetOptions = this.state.widgets.map(widget => {
      return {
        label: widget.getClassName(),
        value: widget.id,
      };
    });

    const widget = this.state.widgets.get(this.state.selected);
    invariant(widget, 'widget null in WidgetSettingsView');
    const withJoinButton = this.state.withJoinButton && !!widget.getGroup();

    const builderParams = {
      height: this.state.widgetHeight,
      join: withJoinButton,
    };

    const iframeSource = `${widget.getWidgetUrl()}&${qs.stringify(builderParams)}`;
    const scriptSource = `${widget.getScriptUrl()}&${qs.stringify(builderParams)}`;
    const googleSource = `${widget.getGadgetUrl()}?${qs.stringify(builderParams)}`;

    let code;

    if (this.state.type === 'google') {
      code = googleSource;
    } else if (this.state.type === 'wix') {
      code = `<div><script src="${scriptSource}"></script></div>`;
    } else {
      code = `<script src="${scriptSource}"></script>`;
    }

    return (
      <Columns2 gutter="xlarge">
        <div>
          <Row size="small">
            <Field>
              <Label>Step 1: Choose a class</Label>
              <Select
                onChange={this.handlerWidgetChange}
                value={this.state.selected}
                options={widgetOptions}
              />
            </Field>
            {this.state.advancedSettingsVisible && (
              <div>
                {widget.getGroup() && (
                  <div>
                    <Row size="xsmall">
                      <Text size="xsmall" weight="semibold">
                        Can users join your class?
                      </Text>
                    </Row>
                    <Field>
                      <Checkbox
                        onChange={this.handlerJoinButtonToggle}
                        checked={withJoinButton}
                        label="Enable subscribers to join my class"
                      />
                    </Field>
                  </div>
                )}
                <Field>
                  <Label>Widget height</Label>
                  <Input
                    type="number"
                    pattern="\d*"
                    width="auto"
                    step="5"
                    value={this.state.widgetHeight}
                    className="WidgetSettingsView-widgetHeightInput"
                    onChange={this.handleWidgetHeightChange}
                  />
                </Field>
                <Field>
                  <Row size="xsmall">
                    <Text size="xsmall" weight="semibold">
                      Type
                    </Text>
                  </Row>
                  <Radio
                    onChange={this.onRegularTypeSelect}
                    name="widget-type"
                    defaultChecked
                    inline>
                    Regular
                  </Radio>
                  <Radio onChange={this.onGoogleTypeSelect} name="widget-type" inline>
                    Google site
                  </Radio>
                  <Radio onChange={this.onWixTypeSelect} name="widget-type" inline>
                    Wix
                  </Radio>
                </Field>
              </div>
            )}
            <Button
              color="brand"
              level="text"
              size="small"
              weight="semibold"
              className="WidgetSettingsView-advancedSettingsButton"
              onClick={this.handleToggleAdvancedSettings}
              eventElementName="WidgetSettingsView-advancedSettingsButton">
              {this.state.advancedSettingsVisible
                ? 'Hide advanced settings'
                : 'Show advanced settings'}
            </Button>
            <Divider />
            <Field>
              <Label>Step 2: Copy the code to your website</Label>
              <div className="WidgetSettingsView-code">
                <code>{code}</code>
              </div>
            </Field>
            <ClipboardTrigger text={code} onCopy={this.handlerCodeCopy}>
              <Button full>Copy widget code</Button>
            </ClipboardTrigger>
          </Row>
          <p>
            <Link href={LinkConstants.WIDGET_INSTRUCTIONS} target="_blank">
              Click here
            </Link>{' '}
            for instructions on how to add the widget.
          </p>
        </div>
        <div>
          <Row size="xsmall">
            <Text size="xsmall" weight="semibold">
              Live preview
            </Text>
          </Row>
          <IframePreview
            src={iframeSource}
            height={this.state.widgetHeight}
            title="Remind widget preview"
          />
        </div>
      </Columns2>
    );
  }

  renderGroupCreateLink() {
    if (!this.state.canCreateGroups) {
      return null;
    }

    return (
      <ModalTrigger modal={<GroupCreateModal onCreate={this.handleGroupCreated} />}>
        <Link>Create your first class.</Link>
      </ModalTrigger>
    );
  }

  render() {
    let headerText =
      'Embed any Remind widget into the HTML of your class website or blog to display your recently sent messages.';

    if (this.state.widgets.length === 0) {
      headerText = (
        <Text color="eclipse">
          Widgets will appear here once you create your first class. {this.renderGroupCreateLink()}
        </Text>
      );
    }

    return (
      <div className={cx('WidgetSettingsView', this.props.className)}>
        <SettingsContainer>
          <Legend>
            Class widgets
            <p>{headerText}</p>
          </Legend>
          {this.state.widgets.length > 0 && this.renderBuilder()}
        </SettingsContainer>
      </div>
    );
  }
}

export default waitForPromises(Container.create(WidgetSettingsView));
